import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatRp, API } from "@/lib/api";
import { assetUrl } from "@/lib/settings";
import { Store, Plus, Upload, Trash2, Edit3, Package } from "lucide-react";

const EMPTY = { name:"", description:"", price:0, stock:0, unit:"pcs", image_url:"", active:true };

export default function SellerProducts() {
  const [products, setProducts] = useState([]);
  const [dialog, setDialog] = useState({ open:false, editing:null, form: EMPTY });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/seller/products"); setProducts(r.data); }
    catch { toast.error("Gagal load produk"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setDialog({ open:true, editing:null, form: {...EMPTY} });
  const openEdit = (p) => setDialog({ open:true, editing:p, form: {...p} });
  const close = () => setDialog({ open:false, editing:null, form: EMPTY });

  const save = async () => {
    const f = dialog.form;
    if (!f.name || !f.price) { toast.error("Nama & harga wajib"); return; }
    const body = { ...f, price: parseInt(f.price)||0, stock: parseInt(f.stock)||0 };
    try {
      if (dialog.editing) await api.put(`/seller/products/${dialog.editing.id || dialog.editing._id || dialog.editing.name}`, body);
      else await api.post("/seller/products", body);
      toast.success("Produk disimpan");
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };

  const del = async (id) => {
    if (!confirm("Hapus produk ini?")) return;
    try { await api.delete(`/seller/products/${id}`); toast.success("Dihapus"); load(); }
    catch { toast.error("Gagal hapus"); }
  };

  const uploadImg = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/upload`, { method:"POST", body: fd });
      const d = await r.json();
      setDialog(dg => ({ ...dg, form: { ...dg.form, image_url: d.url } }));
      toast.success("Gambar diunggah");
    } catch { toast.error("Upload gagal"); }
    setUploading(false);
  };

  return (
    <Card className="glass glow-border bg-transparent rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-white text-lg">Katalog Produk Seller</h3>
          <p className="text-xs text-slate-400">Kelola produk yang Anda jual di tokoinduk.com marketplace</p>
        </div>
        <Button data-testid="seller-add-product" onClick={openNew} className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">
          <Plus className="w-4 h-4 mr-1"/>Tambah Produk
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-14">
          <Store className="w-12 h-12 text-slate-700 mx-auto mb-3"/>
          <div className="text-slate-400">Belum ada produk. Klik "Tambah Produk" untuk mulai jualan.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <div key={p.id || p._id || p.name} data-testid={`prod-${p.name}`} className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
              <div className="h-40 bg-slate-950 flex items-center justify-center">
                {p.image_url ? <img src={assetUrl(p.image_url)} alt={p.name} className="max-h-full max-w-full object-contain"/> : <Package className="w-10 h-10 text-slate-700"/>}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.description}</div>
                  </div>
                  <span className={`text-[10px] font-mono-metric px-2 py-0.5 rounded ${p.active ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{p.active ? "AKTIF" : "NONAKTIF"}</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="font-mono-metric text-cyan-300 font-bold">{formatRp(p.price)}<span className="text-xs text-slate-500">/{p.unit}</span></div>
                    <div className="text-xs text-slate-500 font-mono-metric">Stok {p.stock}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} className="h-8 w-8 text-slate-300 hover:text-cyan-300"><Edit3 className="w-4 h-4"/></Button>
                    <Button data-testid={`del-${p.name}`} size="icon" variant="ghost" onClick={() => del(p.id || p._id)} className="h-8 w-8 text-slate-300 hover:text-red-400"><Trash2 className="w-4 h-4"/></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader><DialogTitle>{dialog.editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-metric text-slate-300">Foto Produk</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="h-20 w-20 bg-slate-950 rounded-md flex items-center justify-center overflow-hidden">
                  {dialog.form.image_url ? <img src={assetUrl(dialog.form.image_url)} alt="" className="max-h-full max-w-full object-contain"/> : <Package className="w-6 h-6 text-slate-700"/>}
                </div>
                <label className="flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 cursor-pointer bg-slate-800 px-3 py-2 rounded-md">
                  <Upload className="w-3 h-3"/>{uploading ? "Mengunggah..." : "Upload gambar"}
                  <input data-testid="prod-image" type="file" accept="image/*" className="hidden" onChange={e => uploadImg(e.target.files?.[0])}/>
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-metric text-slate-300">Nama Produk</Label>
              <Input data-testid="prod-name" value={dialog.form.name} onChange={e=>setDialog(dg=>({...dg, form:{...dg.form, name:e.target.value}}))} className="mt-1 bg-slate-800 border-slate-700"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-metric text-slate-300">Deskripsi</Label>
              <Textarea data-testid="prod-desc" value={dialog.form.description} onChange={e=>setDialog(dg=>({...dg, form:{...dg.form, description:e.target.value}}))} className="mt-1 bg-slate-800 border-slate-700"/>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-metric text-slate-300">Harga</Label>
                <Input data-testid="prod-price" type="number" value={dialog.form.price} onChange={e=>setDialog(dg=>({...dg, form:{...dg.form, price:e.target.value}}))} className="mt-1 bg-slate-800 border-slate-700"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-metric text-slate-300">Stok</Label>
                <Input data-testid="prod-stock" type="number" value={dialog.form.stock} onChange={e=>setDialog(dg=>({...dg, form:{...dg.form, stock:e.target.value}}))} className="mt-1 bg-slate-800 border-slate-700"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-metric text-slate-300">Satuan</Label>
                <Input data-testid="prod-unit" value={dialog.form.unit} onChange={e=>setDialog(dg=>({...dg, form:{...dg.form, unit:e.target.value}}))} className="mt-1 bg-slate-800 border-slate-700"/>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input data-testid="prod-active" type="checkbox" checked={dialog.form.active} onChange={e=>setDialog(dg=>({...dg, form:{...dg.form, active:e.target.checked}}))}/> Aktifkan produk (tampil di marketplace)
            </label>
          </div>
          <Button data-testid="prod-save" onClick={save} className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">Simpan</Button>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
